import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

export interface IFooterCategory {
  id?: number;
  footer_id?: number;
  name: string;
  name_text_color?: string;
  order_index: number;
  text_color?: string;
  background_color?: string;
  status?: boolean;
  is_active?: boolean;
  items?: IFooterItem[];
}

export interface IFooterItem {
  id?: number;
  category_id: number;
  label: string;
  label_color?: string;
  url: string;
  description?: string;
  icon_url?: string;
  icon_alt?: string;
  priority: number;
  status?: boolean;
  is_active?: boolean;
}

export interface IFooter {
  id?: number;
  page_id?: number;
  component_id?: number;
  title?: string;
  title_text_color?: string;
  enable_footer_content?: boolean;
  background_color?: string;
  text_color?: string;
  enable_copyright?: boolean;
  enable_animation?: boolean | number;
  copyright_text?: string;
  copyright_text_color?: string;
  variant?: string;
  status?: boolean;
  categories?: IFooterCategory[];
}

// Category API methods
export async function createFooterCategory(payload: Partial<IFooterCategory>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.FOOTER.CATEGORY.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFooterCategory(id: number, payload: Partial<IFooterCategory>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.FOOTER.CATEGORY.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFooterCategory(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.FOOTER.CATEGORY.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFooterCategoriesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.FOOTER.CATEGORY.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Footer API methods
export async function createFooter(payload: Partial<IFooter>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.FOOTER.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFooter(id: number, payload: Partial<IFooter>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.FOOTER.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFooterById(id: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.FOOTER.GET_BY_ID(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFooterByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.FOOTER.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFooter(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.FOOTER.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Footer Item API methods (for items within categories)
export async function createFooterItem(payload: Partial<IFooterItem>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.FOOTER.ITEM.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateFooterItem(id: number, payload: Partial<IFooterItem>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.FOOTER.ITEM.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getFooterItemsByCategoryId(categoryId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.FOOTER.ITEM.GET_BY_CATEGORY_ID(categoryId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteFooterItem(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.FOOTER.ITEM.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function cloneFooterWithCategories(
  sourceFooterId: number,
  targetPageComponentId: number,
  targetFooterId?: number | null
) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.FOOTER.CLONE,
      {
        source_footer_id: sourceFooterId,
        target_page_component_id: targetPageComponentId,
        target_footer_id: targetFooterId || null,
      }
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}
